/**
 * Base Service
 * Provides common functionality for all API services
 */

import { ENV } from '../config/environment';
import type { ApiResponse } from '../types/api';
import { debug } from '../utils/debug';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

export abstract class BaseService {
  protected readonly baseUrl: string;
  private static requestCache = new Map<string, CacheEntry<any>>();
  private static ongoingRequests = new Map<string, Promise<any>>();

  constructor() {
    this.baseUrl = ENV.API_URL;

    // Debug logging for API configuration
    // Only log once on first initialization
    if (!BaseService.environmentInitialized) {
      debug.log('🔧 BaseService initialized:', {
        baseUrl: this.baseUrl,
        NODE_ENV: ENV.NODE_ENV,
        mode: 'REAL API ONLY'
      });
    }

    // Clean up any cached data from other environments on initialization
    this.initializeEnvironment();
  }

  // Track if environment has been initialized to avoid multiple clears
  private static environmentInitialized = false;

  /**
   * Initialize environment and clean up data from other environments
   */
  private initializeEnvironment(): void {
    if (typeof window !== 'undefined' && !BaseService.environmentInitialized) {
      // Clear tokens from other environments on startup
      this.clearOtherEnvironmentTokens();
      
      // Clear all cache when switching environments
      BaseService.requestCache.clear();
      BaseService.ongoingRequests.clear();
      debug.log('🗑️ Cleared all cache on environment initialization');
      
      BaseService.environmentInitialized = true;
    }
  }

  /**
   * Generate cache key for a request
   */
  private getCacheKey(url: string, options?: RequestInit): string {
    const method = options?.method || 'GET';
    const body = options?.body ? JSON.stringify(options.body) : '';
    return `${method}:${url}:${body}`;
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  /**
   * Get data from cache if valid
   */
  private getFromCache<T>(cacheKey: string): T | null {
    const entry = BaseService.requestCache.get(cacheKey);
    if (entry && this.isCacheValid(entry)) {
      debug.log('📋 Cache HIT for:', cacheKey);
      return entry.data;
    }
    if (entry) {
      BaseService.requestCache.delete(cacheKey);
    }
    return null;
  }

  /**
   * Store data in cache
   */
  private setCache<T>(cacheKey: string, data: T, ttl: number): void {
    BaseService.requestCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl,
    });
    debug.log('💾 Cache SET for:', cacheKey, `(TTL: ${ttl}ms)`);
  }

  /**
   * Clear all cache entries
   */
  protected clearAllCache(): void {
    const cacheSize = BaseService.requestCache.size;
    const ongoingSize = BaseService.ongoingRequests.size;
    
    BaseService.requestCache.clear();
    BaseService.ongoingRequests.clear();
    
    debug.log(`🗑️ All cache cleared: ${cacheSize} cache entries and ${ongoingSize} ongoing requests`);
  }

  /**
   * Clear cache entries by pattern
   */
  protected clearCacheByPattern(patterns: string[]): void {
    let clearedCount = 0;
    
    for (const [key] of BaseService.requestCache) {
      const endpoint = key.split(':')[1]; // Extract endpoint from cache key
      
      if (patterns.some(pattern => endpoint.includes(pattern))) {
        BaseService.requestCache.delete(key);
        clearedCount++;
      }
    }

    // Also clear ongoing requests
    for (const [key] of BaseService.ongoingRequests) {
      const endpoint = key.split(':')[1];
      
      if (patterns.some(pattern => endpoint.includes(pattern))) {
        BaseService.ongoingRequests.delete(key);
      }
    }
    
    debug.log(`🗑️ Selective cache cleared: ${clearedCount} entries for patterns:`, patterns);
  }

  /**
   * Make HTTP requests with caching and deduplication
   */
  protected async fetchApi<T>(
    endpoint: string,
    options: RequestInit = {},
    cacheTTL?: number // Optional cache time-to-live in milliseconds
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
        ...this.getAuthHeaders(),
      } as Record<string, string>;

      // Generate cache key
      const cacheKey = this.getCacheKey(endpoint, { ...options, headers });

      // For GET requests, check cache first
      if ((!options.method || options.method === 'GET') && cacheTTL !== 0) {
        const cachedData = this.getFromCache<ApiResponse<T>>(cacheKey);
        if (cachedData) {
          return cachedData;
        }

        // Check if there's an ongoing request for the same endpoint
        const ongoingRequest = BaseService.ongoingRequests.get(cacheKey);
        if (ongoingRequest) {
          debug.log('⏳ Request DEDUPLICATION for:', endpoint);
          return ongoingRequest;
        }
      }

      // Debug logging for authentication issues
      if (ENV.IS_DEVELOPMENT) {
        debug.log('🔐 API Request:', {
          url,
          method: options.method || 'GET',
          hasAuth: !!headers['Authorization'],
          authPreview: headers['Authorization']?.substring(0, 50) + '...',
          contentType: headers['Content-Type'],
        });
      }

      // Create and store the request promise for deduplication
      const requestPromise = (async () => {
        const response = await fetch(url, {
          headers,
          ...options,
        });

        if (!response.ok) {
          // Enhanced error logging for auth issues
          if (response.status === 401 || response.status === 404) {
            debug.error('🚨 Authentication/User Error:', {
              url,
              status: response.status,
              statusText: response.statusText,
              hasAuthHeader: !!headers['Authorization'],
              storedToken: this.getStoredToken() ? 'EXISTS' : 'MISSING',
            });
            
            // Clear invalid token and notify about auth state change
            this.removeToken();
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new Event('authStateChanged'));
            }
          }
          
          // Try to parse the error response from the backend
          let errorData;
          try {
            const errorText = await response.text();
            errorData = errorText ? JSON.parse(errorText) : { success: false, message: `HTTP ${response.status}: ${response.statusText}` };
          } catch {
            errorData = { success: false, message: `HTTP ${response.status}: ${response.statusText}` };
          }
          
          // Return the error response instead of throwing
          return errorData;
        }

        // Handle 204 No Content responses
        let data;
        if (response.status === 204) {
          data = { success: true, message: 'Operation completed successfully' };
        } else {
          const text = await response.text();
          data = text ? JSON.parse(text) : { success: true, message: 'Operation completed successfully' };
        }
        
        // Cache GET requests if cacheTTL is provided and request was successful
        if ((!options.method || options.method === 'GET') && cacheTTL && cacheTTL > 0) {
          this.setCache(cacheKey, data, cacheTTL);
        }
        
        return data;
      })();

      // Store the promise for deduplication
      if ((!options.method || options.method === 'GET') && cacheTTL !== 0) {
        BaseService.ongoingRequests.set(cacheKey, requestPromise);
        
        // Clean up the ongoing request when it completes
        requestPromise.finally(() => {
          BaseService.ongoingRequests.delete(cacheKey);
        });
      }

      return requestPromise;
    } catch (error) {
      debug.error('API Error:', error);
      throw error;
    }
  }

  /**
   * Simulate delay for mock responses (development only)
   */
  protected async simulateDelay(ms: number = 500): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Create a success response
   */
  protected createSuccessResponse<T>(
    data: T,
    message?: string
  ): ApiResponse<T> {
    return {
      success: true,
      data,
      message: message || 'Success',
    };
  }

  /**
   * Create an error response
   */
  protected createErrorResponse(
    userMessage: string,
    debugInfo?: string
  ): ApiResponse<any> {
    if (ENV.IS_DEVELOPMENT && debugInfo) {
      debug.error('API Error Debug:', debugInfo);
    }
    return {
      success: false,
      message: userMessage,
    };
  }

  /**
   * Get authentication headers
   */
  protected getAuthHeaders(): Record<string, string> {
    const token = this.getStoredToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Debug logging
    if (ENV.IS_DEVELOPMENT) {
      debug.log('🔑 Auth Headers:', {
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 50) + '...' : 'NONE',
        headers: Object.keys(headers),
      });
    }
    
    return headers;
  }

  /**
   * Check if token is expired (basic JWT validation)
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true; // If we can't parse it, consider it expired
    }
  }

  /**
   * Get stored authentication token
   */
  protected getStoredToken(): string | null {
    if (typeof window === 'undefined') return null;

    // Try different storage keys for backward compatibility
    const storageKeys = ['auth_token_prod', 'auth_token_dev', 'auth_token'];
    
    for (const key of storageKeys) {
      const token = localStorage.getItem(key);
      if (token) {
        // Check if token is expired
        if (this.isTokenExpired(token)) {
          debug.warn('⚠️ Token is expired, removing from storage');
          localStorage.removeItem(key);
          continue;
        }
        
        // If we found a token in a non-standard key, migrate it
        if (key !== 'auth_token_prod') {
          localStorage.setItem('auth_token_prod', token);
          if (key !== 'auth_token_prod') {
            localStorage.removeItem(key);
          }
        }
        return token;
      }
    }
    
    // Debug logging
    if (ENV.IS_DEVELOPMENT) {
      debug.warn('⚠️ No valid auth token found in any storage key');
    }
    
    return null;
  }

  /**
   * Store authentication token
   */
  protected storeToken(token: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token_prod', token);
      
      // Debug logging
      if (ENV.IS_DEVELOPMENT) {
        debug.log('💾 Token stored:', {
          tokenPreview: token.substring(0, 30) + '...',
        });
      }
    }
  }

  /**
   * Remove authentication token
   */
  protected removeToken(): void {
    if (typeof window !== 'undefined') {
      // Remove all possible token keys
      const tokenKeys = ['auth_token_prod', 'auth_token_dev', 'auth_token'];
      tokenKeys.forEach(key => localStorage.removeItem(key));
      
      debug.log('🗑️ All auth tokens removed');
    }
  }

  /**
   * Clear tokens from other environments
   */
  private clearOtherEnvironmentTokens(): void {
    if (typeof window !== 'undefined') {
      // Clear any old/test tokens
      const oldKeys = ['mock_user_dev', 'mock_user_prod'];
      oldKeys.forEach(key => localStorage.removeItem(key));
      
      debug.log('🧹 Cleared environment-specific tokens');
    }
  }
}